import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { toast } from 'sonner';
import { getTodosList } from '../endpoints/todos/list_GET.schema';
import { createTodo } from '../endpoints/todos/create_POST.schema';
import { updateTodo } from '../endpoints/todos/update_POST.schema';
import { deleteTodo } from '../endpoints/todos/delete_POST.schema';
import { createTodoStep } from '../endpoints/todos/steps/create_POST.schema';
import { updateTodoStep } from '../endpoints/todos/steps/update_POST.schema';
import { deleteTodoStep } from '../endpoints/todos/steps/delete_POST.schema';

export const useTodos = (sessionId: string | undefined) => {
  return useQuery({
    queryKey: ['todos', 'list', sessionId],
    queryFn: () => {
      if (!sessionId) throw new Error('Session ID is required');
      return getTodosList({ sessionId });
    },
    enabled: !!sessionId,
    retry: 1,
    // Poll every 2 seconds if any todos are processing
    // This ensures UI updates when background AI generation completes
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.todos) return false;
      const hasProcessing = data.todos.some((t: any) => t.generationStatus === 'processing');
      return hasProcessing ? 1000 : false; // Poll every 1s while processing, stop when done
    },
  });
};

export const useCreateTodo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTodo,
    onSuccess: (data: any) => {
      // Todo created - AI generation runs automatically in background
      toast.success('Todo created - AI generating...');
      queryClient.invalidateQueries({ queryKey: ['todos', 'list', data.todo.sessionId] });
    },
    onError: (error) => {
      toast.error(`Failed to create todo: ${error.message}`);
    },
  });
};

export const useUpdateTodo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTodo,
    onSuccess: (data) => {
      // We don't have sessionId in the return directly if we just return the todo,
      // but we can invalidate all todo lists or try to read from cache.
      // Since todo has sessionId, we can use it.
      queryClient.invalidateQueries({ queryKey: ['todos', 'list', data.todo.sessionId] });
    },
    onError: (error) => {
      toast.error(`Failed to update todo: ${error.message}`);
    },
  });
};

export const useDeleteTodo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTodo,
    onSuccess: () => {
      toast.success('Todo deleted');
      // Since we don't know the sessionId from the delete response (it just returns success),
      // we invalidate all todo lists. This is safe but slightly less efficient.
      queryClient.invalidateQueries({ queryKey: ['todos', 'list'] });
    },
    onError: (error) => {
      toast.error(`Failed to delete todo: ${error.message}`);
    },
  });
};

export const useCreateStep = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTodoStep,
    onSuccess: () => {
      toast.success('Step added');
      // Invalidate all todo lists to refresh the steps
      queryClient.invalidateQueries({ queryKey: ['todos', 'list'] });
    },
    onError: (error) => {
      toast.error(`Failed to add step: ${error.message}`);
    },
  });
};

export const useUpdateStep = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTodoStep,
    onSuccess: () => {
      // Invalidate all todo lists to refresh the steps
      queryClient.invalidateQueries({ queryKey: ['todos', 'list'] });
    },
    onError: (error) => {
      toast.error(`Failed to update step: ${error.message}`);
    },
  });
};

export const useDeleteStep = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTodoStep,
    onSuccess: () => {
      toast.success('Step deleted');
      // Invalidate all todo lists to refresh the steps
      queryClient.invalidateQueries({ queryKey: ['todos', 'list'] });
    },
    onError: (error) => {
      toast.error(`Failed to delete step: ${error.message}`);
    },
  });
};

// Process all todos with AI
const processAllTodos = async (params: { sessionId: string; todoIds?: string[] }) => {
  const response = await fetch('/_api/todos/process-all', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to process todos');
  }
  return response.json();
};

export const useProcessAllTodos = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: processAllTodos,
    onSuccess: (data, variables) => {
      toast.success(`Processing ${data.processed} todos with AI`);
      // Start polling for updates
      queryClient.invalidateQueries({ queryKey: ['todos', 'list', variables.sessionId] });
      // Also refresh the session files and sessions details so LivePreview can pick up changes
      queryClient.invalidateQueries({ queryKey: ['files', 'list', variables.sessionId] });
      queryClient.invalidateQueries({ queryKey: ['sessions', 'detail', variables.sessionId] });
    },
    onError: (error) => {
      toast.error(`Failed to process todos: ${error.message}`);
    },
  });
};
